import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api, apiErrorText, endpoints, isApiError } from "../lib/api";
import { isRecord, listFrom, text, unwrapData } from "../lib/data";
import { revokeNativePushToken } from "../native/platform";

export type AppRole = "USER" | "MODERATOR" | "ADMIN";

export interface Viewer {
  id: string;
  username: string;
  email: string;
  status: string;
  roles: AppRole[];
  avatarUrl: string | null;
  balanceMinor: number;
  unreadNotifications: number;
  capabilities: Record<string, boolean>;
  capabilityReasons: Record<string, string>;
  setupSteps: string[];
  securityNotice: {
    kind: "ADMIN_ALTERNATIVE_CODE_USED";
    position: number;
    message: string;
  } | null;
}

type AuthState =
  | { kind: "loading" }
  | { kind: "anonymous" }
  | { kind: "authenticated"; viewer: Viewer }
  | { kind: "unavailable"; message: string };

interface AuthContextValue {
  state: AuthState;
  refresh: () => Promise<void>;
  login: (
    identifier: string,
    password?: string,
    options?: {
      secondFactor?: string;
      trustDevice?: boolean;
      securityAnswers?: { questionId: string; answer: string }[];
    },
  ) => Promise<Viewer>;
  logout: () => Promise<void>;
  dismissSecurityNotice: () => void;
  can: (capability: string) => boolean;
  reason: (capability: string) => string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function normalizeRoles(value: unknown): AppRole[] {
  const source = Array.isArray(value) ? value : [];
  const roles = source
    .map((entry) => {
      if (typeof entry === "string") return entry.toUpperCase();
      if (isRecord(entry)) return text(entry.name ?? entry.role, "").toUpperCase();
      return "";
    })
    .filter((role): role is AppRole => role === "USER" || role === "MODERATOR" || role === "ADMIN");
  return roles.length ? Array.from(new Set(roles)) : ["USER"];
}

function normalizeCapabilities(value: unknown): {
  allowed: Record<string, boolean>;
  reasons: Record<string, string>;
} {
  const allowed: Record<string, boolean> = {};
  const reasons: Record<string, string> = {};
  if (Array.isArray(value)) {
    value.forEach((entry) => {
      if (typeof entry === "string") allowed[entry] = true;
      if (isRecord(entry)) {
        const key = text(entry.key ?? entry.name ?? entry.capability, "");
        if (!key) return;
        allowed[key] = entry.allowed !== false;
        if (typeof entry.reason === "string") reasons[key] = entry.reason;
      }
    });
  } else if (isRecord(value)) {
    Object.entries(value).forEach(([key, entry]) => {
      if (typeof entry === "boolean") allowed[key] = entry;
      if (isRecord(entry)) {
        allowed[key] = entry.allowed === true;
        if (typeof entry.reason === "string") reasons[key] = entry.reason;
      }
    });
  }
  return { allowed, reasons };
}

function normalizeViewer(payload: unknown): Viewer {
  const body = unwrapData(payload);
  if (!isRecord(body)) throw new Error("Die Sitzungsantwort des Servers ist ungültig.");
  const source = isRecord(body.user) ? body.user : isRecord(body.viewer) ? body.viewer : body;
  const capabilitySource = body.capabilities ?? source.capabilities;
  const normalized = normalizeCapabilities(capabilitySource);
  const bootstrap = isRecord(body.bootstrap)
    ? body.bootstrap
    : isRecord(source.bootstrap)
      ? source.bootstrap
      : undefined;
  const explicitSteps = listFrom(bootstrap, "steps")
    .map((entry) => text(entry.key ?? entry.name, ""))
    .filter(Boolean);
  const bootstrapStage = text(body.bootstrapStage ?? source.bootstrapStage, "").toUpperCase();
  const setupSteps = explicitSteps.length
    ? explicitSteps
    : [
        bootstrapStage === "RECOVERY" ? "SECURITY_QUESTIONS" : bootstrapStage,
        source.forcePasswordChange === true || source.mustChangePassword === true ? "PASSWORD" : "",
        source.mustSetupTotp === true ? "TOTP" : "",
        source.mustSetupSecurityQuestions === true ? "SECURITY_QUESTIONS" : "",
      ].filter(Boolean);
  const roles = normalizeRoles(source.roles ?? (source.role ? [source.role] : []));
  const rawBalance = source.balanceMinor ?? source.balanceCents ?? source.walletBalanceMinor ?? 0;
  const rawSecurityNotice = isRecord(body.securityNotice)
    ? body.securityNotice
    : isRecord(source.securityNotice)
      ? source.securityNotice
      : null;
  const securityNotice =
    rawSecurityNotice?.kind === "ADMIN_ALTERNATIVE_CODE_USED" &&
    Number.isInteger(Number(rawSecurityNotice.position))
      ? {
          kind: "ADMIN_ALTERNATIVE_CODE_USED" as const,
          position: Number(rawSecurityNotice.position),
          message: text(
            rawSecurityNotice.message,
            "Ein Admin-Alternativcode wurde für diese Anmeldung verwendet.",
          ),
        }
      : null;
  return {
    id: text(source.id, ""),
    username: text(source.username, "Unbekannter Nutzer"),
    email: text(source.email, ""),
    status: text(source.status, "ACTIVE").toUpperCase(),
    roles,
    avatarUrl: typeof source.avatarUrl === "string" ? source.avatarUrl : null,
    balanceMinor: typeof rawBalance === "number" ? rawBalance : Number(rawBalance) || 0,
    unreadNotifications:
      typeof source.unreadNotifications === "number" ? source.unreadNotifications : 0,
    capabilities: normalized.allowed,
    capabilityReasons: normalized.reasons,
    setupSteps,
    securityNotice,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ kind: "loading" });

  const refresh = useCallback(async () => {
    try {
      const response = await api.get<unknown>(endpoints.me);
      setState({ kind: "authenticated", viewer: normalizeViewer(response) });
    } catch (error) {
      if (isApiError(error) && error.status === 401) {
        setState({ kind: "anonymous" });
      } else {
        setState({ kind: "unavailable", message: apiErrorText(error) });
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (
      identifier: string,
      password?: string,
      options?: {
        secondFactor?: string;
        trustDevice?: boolean;
        securityAnswers?: { questionId: string; answer: string }[];
      },
    ) => {
      await api.get(endpoints.csrf);
      const response = await api.post<unknown>(endpoints.login, {
        identifier,
        ...(password ? { password } : {}),
        ...(options?.secondFactor ? { secondFactor: options.secondFactor } : {}),
        ...(options?.trustDevice !== undefined ? { trustDevice: options.trustDevice } : {}),
        ...(options?.securityAnswers ? { securityAnswers: options.securityAnswers } : {}),
      });
      const viewer = normalizeViewer(response);
      setState({ kind: "authenticated", viewer });
      return viewer;
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await revokeNativePushToken();
      await api.post(endpoints.logout);
    } finally {
      setState({ kind: "anonymous" });
    }
  }, []);

  const dismissSecurityNotice = useCallback(() => {
    setState((current) =>
      current.kind === "authenticated"
        ? {
            ...current,
            viewer: { ...current.viewer, securityNotice: null },
          }
        : current,
    );
  }, []);

  const can = useCallback(
    (capability: string) => {
      if (state.kind !== "authenticated") return false;
      if (capability in state.viewer.capabilities)
        return state.viewer.capabilities[capability] === true;
      if (capability.startsWith("admin.")) return state.viewer.roles.includes("ADMIN");
      if (capability.startsWith("team.")) {
        return state.viewer.roles.includes("ADMIN") || state.viewer.roles.includes("MODERATOR");
      }
      return false;
    },
    [state],
  );

  const reason = useCallback(
    (capability: string) => {
      if (state.kind !== "authenticated") return "Bitte zuerst anmelden.";
      return (
        state.viewer.capabilityReasons[capability] ??
        (capability.startsWith("admin.")
          ? "Diese Funktion ist ausschließlich für den Admin verfügbar."
          : capability.startsWith("team.")
            ? "Dafür ist eine Team-Berechtigung erforderlich."
            : "Der Server hat diese Funktion für dein Konto nicht freigegeben.")
      );
    },
    [state],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ state, refresh, login, logout, dismissSecurityNotice, can, reason }),
    [state, refresh, login, logout, dismissSecurityNotice, can, reason],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth muss innerhalb des AuthProvider verwendet werden.");
  return value;
}
